import { Component, OnDestroy, OnInit } from '@angular/core';
import { CustomCheckoutDeliveryAddressComponent } from '../checkout-delivery-address/checkout-delivery-address.component';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CheckoutStepService } from '@spartacus/checkout/base/components';
import { CustomValidatorService } from '../../../../../../components/form-elements/custom-validator.service';
import { BehaviorSubject, combineLatest, Subscription, take } from 'rxjs';
import { Option } from '../../../../../../core/models/option.model';
import { ModalService } from '../../../../../../components/modal-list/modal-list.service';
import { CustomActiveCartService } from '../../../../cart/_core/active-cart.service';
import { CheckoutAuxService } from '../../../b2b/core/checkout-aux.service';
import { ToasterService } from '../../../../../../components/toaster/toaster.service';
import { TOASTER_TYPE } from '../../../../../../components/toaster/toaster.model';
import { Cart } from '@spartacus/cart/base/root';
import { ModalAddressConfirmationComponent } from './components/modal-address-confirmation/modal-address-confirmation.component';

@Component({
  selector: 'checkout-preview-shipping-address',
  templateUrl: './checkout-preview-shipping-address.component.html',
  styleUrl: './checkout-preview-shipping-address.component.scss',
  standalone: false,
})
export class CheckoutPreviewShippingAddressComponent implements OnInit, OnDestroy {
  subscription = new Subscription();
  cart$ = new BehaviorSubject<Cart>({});
  oldPreference = '';
  preferences$ = new BehaviorSubject<Option[]>([]);

  form: FormGroup = this.fb.group({
    shippingPreference: ['', [this.customValidatorService.required()]],
  });

  constructor(
    private activatedRoute: ActivatedRoute,
    private checkoutStepService: CheckoutStepService,
    private modalService: ModalService,
    private customValidatorService: CustomValidatorService,
    private fb: FormBuilder,
    private customActiveCartService: CustomActiveCartService,
    private checkoutAuxService: CheckoutAuxService,
    private toasterService: ToasterService,
  ) {}

  ngOnInit(): void {
    this.populateComponent();
    this.handlerChangeShippingPreference();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  populateComponent() {
    this.getShippingPreferences();

    this.subscription.add(
      this.customActiveCartService.getActive().subscribe(cart => {
        this.cart$.next(cart);
      }),
    );
  }

  getShippingPreferences() {
    this.subscription.add(
      combineLatest([this.checkoutAuxService.getShippingPreferences(), this.cart$]).subscribe(
        ([reponsePreferences, cart]) => {
          if (reponsePreferences.hasError) {
            this.toasterService.init({
              type: TOASTER_TYPE.DANGER,
              content: reponsePreferences.message!,
            });
          } else {
            this.preferences$.next(reponsePreferences.preferences || []);

            if (cart?.shippingPreference) {
              this.oldPreference = cart.shippingPreference.code;

              this.form.patchValue({
                shippingPreference: cart.shippingPreference.code,
              });
            }
          }
        },
      ),
    );
  }

  setShippingPreference(newPreference: string) {
    this.checkoutAuxService
      .setShippingPreference(newPreference)
      .pipe(take(1))
      .subscribe(response => {
        if (response.hasError) {
          this.toasterService.init({
            type: TOASTER_TYPE.DANGER,
            content: response.message!,
          });
        } else {
          this.customActiveCartService.reloadActiveCart();
        }
      });
  }

  handlerChangeShippingPreference() {
    this.subscription.add(
      this.form.get('shippingPreference')!.valueChanges.subscribe((newPreference: string) => {
        if (newPreference !== this.oldPreference) {
          this.setShippingPreference(newPreference);
        }
      }),
    );
  }

  openAddressesModal() {
    this.modalService.create({
      id: 'addresses-modal',
      title: '',
      width: 1200,
      classList: 'addresses-modal',
      component: CustomCheckoutDeliveryAddressComponent,
    });
  }

  back(): void {
    this.checkoutStepService.back(this.activatedRoute);
  }

  confirmAddress(): void {
    if (this.cart$.getValue().deliveryAddress) {
      this.modalService.create({
        id: 'addresses-confirmation-modal',
        title: '',
        width: 900,
        classList: 'addresses-confirmation-modal',
        component: ModalAddressConfirmationComponent,
        data: {
          address: this.cart$.getValue().deliveryAddress,
          next: this.next.bind(this),
        },
      });
    } else {
      this.next();
    }
  }

  next(): void {
    this.form.markAllAsTouched();
    const hasAddress = this.cart$.getValue().deliveryAddress;
    const isOk = this.form.valid && hasAddress;

    if (isOk) {
      this.checkoutStepService.next(this.activatedRoute);
    }
  }
}
